import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, CheckCircle, DollarSign, Headphones, Heart, Server, TrendingUp, Users, Zap } from 'lucide-react';

export default function InfrastructureTab({ scaleData, selectedScale, setSelectedScale, current, formatCurrency }) {
  return (
    <>
              {/* Scale Selector */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Platform Scale
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(scaleData).map(([key, data]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedScale(key)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedScale === key 
                            ? 'border-primary bg-primary/5' 
                            : 'border-transparent bg-muted hover:bg-muted/80'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full ${data.color} mx-auto mb-2`} />
                        <div className="font-bold">{data.users}</div>
                        <div className="text-xs text-muted-foreground">{data.label}</div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cost Summary */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Infrastructure</p>
                        <p className="text-3xl font-bold text-blue-700">{formatCurrency(current.infra.total)}</p>
                        <p className="text-xs text-blue-500">per month</p>
                      </div>
                      <Server className="w-10 h-10 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Labor & Staff</p>
                        <p className="text-3xl font-bold text-purple-700">{formatCurrency(current.labor.total)}</p>
                        <p className="text-xs text-purple-500">{current.staff} people</p>
                      </div>
                      <Headphones className="w-10 h-10 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-600 font-medium">Total Monthly</p>
                        <p className="text-3xl font-bold text-amber-700">{formatCurrency(current.infra.total + current.labor.total)}</p>
                        <p className="text-xs text-amber-500">operating cost</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Cost Per User</p>
                        <p className="text-3xl font-bold text-green-700">${current.perUser.toFixed(2)}</p>
                        <p className="text-xs text-green-500">per month</p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Infrastructure Breakdown at {current.users} Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Compute (Servers)', value: current.infra.compute, icon: Server, color: 'bg-blue-500' },
                      { label: 'Database (MongoDB)', value: current.infra.database, icon: HardDrive, color: 'bg-green-500' },
                      { label: 'File Storage', value: current.infra.storage, icon: HardDrive, color: 'bg-purple-500' },
                      { label: 'CDN & Bandwidth', value: current.infra.cdn, icon: Globe, color: 'bg-amber-500' },
                      { label: 'AI/LLM Services', value: current.infra.ai, icon: Bot, color: 'bg-pink-500' },
                      { label: 'Search (Elasticsearch)', value: current.infra.search, icon: Search, color: 'bg-cyan-500' },
                      { label: 'Other (Monitoring, etc.)', value: current.infra.other, icon: Shield, color: 'bg-gray-500' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center`}>
                          <item.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium">{item.label}</span>
                            <span className="font-bold">${item.value.toLocaleString()}/mo</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${item.color}`}
                              style={{ width: `${(item.value / current.infra.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Labor Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Labor Breakdown at {current.users} Users</CardTitle>
                </CardHeader>
                <CardContent>
                  {current.labor.total === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Heart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>At this stage, the founder handles all operations</p>
                      <p className="text-sm">No additional labor costs</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[
                        { label: 'User Support', value: current.labor.support, icon: Headphones, color: 'bg-blue-500' },
                        { label: 'Content Moderation', value: current.labor.moderation, icon: Eye, color: 'bg-red-500' },
                        { label: 'Community Management', value: current.labor.community, icon: Heart, color: 'bg-pink-500' },
                        { label: 'DevOps/Engineering', value: current.labor.devops, icon: Code, color: 'bg-green-500' },
                        { label: 'Trust & Safety', value: current.labor.trustSafety, icon: Shield, color: 'bg-purple-500' },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center`}>
                            <item.icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-medium">{item.label}</span>
                              <span className="font-bold">${item.value.toLocaleString()}/mo</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${item.color}`}
                                style={{ width: `${(item.value / current.labor.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Scaling Roadmap */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Infrastructure Scaling Roadmap
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {[
                      { scale: '0 - 100K', title: 'Foundation', cost: '$200/mo', items: ['Database indexes', 'Connection pooling', 'Basic monitoring', 'Single server'] },
                      { scale: '500K', title: 'Growth', cost: '$1,000/mo', items: ['Redis caching', 'CDN for assets', 'Rate limiting', 'Image compression'] },
                      { scale: '1M', title: 'Scale', cost: '$2,000/mo', items: ['Read replicas', 'Load balancer + 3 servers', 'Elasticsearch', 'S3 storage'] },
                      { scale: '10M', title: 'Enterprise', cost: '$16,000/mo', items: ['Database sharding', 'Multi-region', 'Akamai CDN', 'Microservices'] }
                    ].map((phase, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-green-500' : 'bg-muted'} text-white font-bold text-sm`}>
                            {idx === 0 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                          </div>
                          {idx < 3 && <div className="w-0.5 h-full bg-muted mt-2" />}
                        </div>
                        <div className="flex-1 pb-6">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{phase.scale} users</Badge>
                            <span className="font-bold">{phase.title}</span>
                            <span className="text-sm text-muted-foreground">({phase.cost} infra)</span>
                          </div>
                          <ul className="grid md:grid-cols-2 gap-1">
                            {phase.items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <ArrowRight className="w-3 h-3" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
    </>
  );
}
